import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Grid, IconButton, InputAdornment, Typography } from '@mui/material';
import FormikAutosuggest from 'components/formik/Autosuggest';
import Switch from 'components/formik/Switch';
import FormikTableField from 'components/formik/TableField';
import TextField from 'components/formik/TextField';
import { Field, useFormikContext } from 'formik';
import React, { memo, useCallback, useMemo, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useSelector } from 'react-redux';
import { I18n } from 'utils/i18n';
import employees from 'store/app/entities/employees/action';
import { useLazyListQuery } from 'store/app/entities/GroupsSlice';
import { useLazyListTeamsQuery } from 'store/app/entities/TeamsSlice';
import isCompanyAdminSelector from 'store/selectors/isCompanyAdmin';
import isSuperAdminSelector from 'store/selectors/isSuperAdmin';
import loadOptionsLazyQuery from 'utils/loadOptionsLazyQuery';
import useActions from 'utils/useActions';
import { useDebounceCallback } from 'utils/useDebounceCallback';
import { mapChannelPickerOption } from './mapChannelPickerOption';
import { isRuleManagedTeamValue, mapTeamPickerOption } from './mapTeamPickerOption';

const fieldSx = { padding: '5px' };

const labelSx = {
  display: 'flex',
  alignItems: 'flex-start',
  mt: '30px',
};

const switchSx = {
  mt: '15px',
};

const perPage = 99;

const ChannelsControls = () => {
  const isCompanyAdmin = useSelector(isCompanyAdminSelector);
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const previewChannels = useActions(employees.previewChannels);
  const { values } = useFormikContext();
  const [showPassword, setShowPassword] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);

  const teamsColumns = useMemo(
    () => [
      {
        key: 'name',
        disablePadding: false,
        label: I18n.t('EmployeeDrawer.TeamsTable.TeamName'),
        xsVisible: true,
      },
      {
        key: 'role',
        disablePadding: false,
        label: I18n.t('EmployeeDrawer.TeamsTable.Role'),
        xsVisible: true,
        type: 'dropdown',
        options: ['member', 'admin', 'guest'].map(value => ({
          value,
          label: I18n.t(`Roles.${value}`),
        })),
        defaultValue: val => val.defaultRole,
      },
      {
        key: 'delete',
        xsVisible: true,
        type: 'remove',
      },
    ],
    [],
  );

  const chatsColumns = useMemo(
    () => [
      {
        key: 'name',
        disablePadding: false,
        label: I18n.t('EmployeeDrawer.GroupsTable.GroupName'),
        xsVisible: true,
      },
      {
        key: 'groupRole',
        disablePadding: false,
        label: I18n.t('EmployeeDrawer.GroupsTable.Role'),
        xsVisible: true,
        type: 'dropdown',
        options: ['member', 'writer', 'admin'].map(value => ({
          value,
          label: I18n.t(`Roles.${value}`),
        })),
        defaultValue: 'member',
      },
      {
        key: 'remove',
        xsVisible: true,
        type: 'remove',
      },
    ],
    [],
  );

  const onGroupCheckboxChange = useCallback((formik, value, index) => {
    if (value[index].value.isAdmin && value[index].value.canWriteAsChannel !== null) {
      const newValue = [...value];
      newValue[index].value.canWriteAsChannel = null;
      formik.setFieldValue('channels', newValue);
    }
    if (!value[index].value.isAdmin && value[index].value.canWriteAsChannel === null) {
      const newValue = [...value];
      newValue[index].value.canWriteAsChannel = false;
      formik.setFieldValue('channels', newValue);
    }
  }, []);

  const handlePreviewChannels = useCallback(
    async (form, id, data) => {
      setChannelsLoading(true);
      const { value } = await previewChannels(id, data);
      form.setFieldValue(
        'channels',
        value
          .filter(c => !c.isDirect && !form.values.channelsRemoved?.includes(c._id))
          .map(c => ({
            label: c.name + (c.description ? ` - ${c.description}` : ''),
            value: { ...c, name: c.name + (c.description ? ` - ${c.description}` : ''), id: c._id },
          })),
      );
      setChannelsLoading(false);
    },
    [previewChannels],
  );
  const previewChannelsDebounced = useDebounceCallback(handlePreviewChannels, 500);

  const onTeamsChange = useCallback(
    (form, fieldName, fieldValue, fieldRemovedValue, fieldAddedValue) => {
      const directChannels = form.values.channels
        .filter(c => !(c.value.fromTeams?.length > 0))
        .map(({ value: { _id, groupRole } }) => ({
          _id,
          canWriteAsChannel: groupRole === 'writer' ? 1 : 0,
          isAdmin: groupRole === 'admin' ? 1 : 0,
        }));

      previewChannelsDebounced(form, form.values._id, {
        teams: fieldAddedValue.map(({ value: { _id, role = 'member', name } }) => ({
          _id,
          role,
          name,
        })),
        teamsRemoved: fieldRemovedValue,
        channels: directChannels,
      });
    },
    [previewChannelsDebounced],
  );

  const onTeamCheckboxChange = useCallback((formik, value) => {
    formik.setFieldValue('teams', value);
  }, []);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(v => !v);
  }, []);

  const handleMouseDownPassword = useCallback(event => {
    event.preventDefault();
  }, []);

  const [triggerListGroupsQuery] = useLazyListQuery();
  const [triggerListTeamsQuery] = useLazyListTeamsQuery();

  const loadTeams = useCallback(async (search, loadedOptions, additional) => {
    const result = await loadOptionsLazyQuery(search, loadedOptions, additional);
    return {
      ...result,
      options: result.options.map(mapTeamPickerOption),
    };
  }, []);

  const loadChannels = useCallback(
    async (search, loadedOptions, additional) => {
      const page = additional?.page || 1;
      const result = await triggerListGroupsQuery({ search, page, perPage, admin: 1 }).unwrap();
      return {
        options: result.groups
          .filter(channel => values.channels?.every(c => c.value.id !== channel._id))
          .map(mapChannelPickerOption),
        hasMore: result.totalCount > (page + 1) * perPage,
        additional: { page: page + 1 },
      };
    },
    [triggerListGroupsQuery, values.channels],
  );

  return (
    <Box sx={{ pt: '20px' }}>
      <Typography variant="caption">
        {I18n.t('EmployeeDrawer.GroupsChannelsPermissions')}
      </Typography>
      <Grid container>
        {isCompanyAdmin ? (
          <>
            <Grid sx={labelSx} size={2}>
              <Typography>{I18n.t('EmployeeDrawer.CompanyAdmin')}</Typography>
            </Grid>
            <Grid size={10}>
              <Box sx={switchSx}>
                <Field component={Switch} name="isAdmin" multiple={false} />
              </Box>
            </Grid>
          </>
        ) : null}
        {isSuperAdmin ? (
          <>
            <Grid sx={labelSx} size={2}>
              <Typography>{I18n.t('EmployeeDrawer.LoginType')}</Typography>
            </Grid>
            <Grid size={10}>
              <Box sx={switchSx}>
                <Box sx={fieldSx}>
                  <Field
                    component={FormikAutosuggest}
                    name="loginType"
                    suggestions={['sms', 'password'].map(value => ({
                      label: value,
                      value,
                    }))}
                  />
                </Box>
              </Box>
            </Grid>
            {values.loginType === 'password' && (
              <>
                <Grid sx={labelSx} size={2}>
                  <Typography>{I18n.t('EmployeeDrawer.NewPassword')}</Typography>
                </Grid>
                <Grid size={10}>
                  <Box sx={switchSx}>
                    <Field
                      component={TextField}
                      sx={fieldSx}
                      type={showPassword ? 'text' : 'password'}
                      id="new-password"
                      autoComplete="new-password"
                      name="newPassword"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </Box>
                </Grid>
              </>
            )}
          </>
        ) : null}
        <>
          <Grid sx={labelSx} size={2}>
            <Typography>
              {`${I18n.t('EmployeeDrawer.Teams')}${isCompanyAdmin ? '' : '*'}`}
            </Typography>
          </Grid>
          <Grid size={10}>
            <Field
              component={FormikTableField}
              loadOptions={loadTeams}
              additional={{
                page: 1,
                exclude: values.teams,
                trigger: triggerListTeamsQuery,
                perPage,
              }}
              onChange={onTeamsChange}
              columns={teamsColumns}
              onCheckboxChange={onTeamCheckboxChange}
              removeDisabled={isRuleManagedTeamValue}
              controlsDisabled={isRuleManagedTeamValue}
              disabledTooltip={I18n.t('EmployeeDrawer.RuleManagedTeam')}
              buttonText={I18n.t('EmployeeDrawer.Add team')}
              placeholder={I18n.t('EmployeeDrawer.Start typing')}
              name="teams"
            />
          </Grid>
        </>
        <Grid sx={labelSx} size={2}>
          <Typography>{I18n.t('EmployeeDrawer.Channels')}</Typography>
        </Grid>
        <Grid size={10}>
          {channelsLoading ? (
            <ContentLoader style={{ flex: '0 0 auto' }} width={670} viewBox="0 0 420 150">
              <rect x="0" y="16" rx="6" ry="6" width="334" height="24" />
              <rect x="340" y="16" rx="6" ry="6" width="60" height="24" />
              <rect x="0" y="62" rx="6" ry="6" width="400" height="80" />
            </ContentLoader>
          ) : (
            <Field
              component={FormikTableField}
              loadOptions={loadChannels}
              controlsDisabled={value => !value.canAdmin}
              additional={{ page: 1 }}
              removeDisabled={value => value.fromTeams?.length > 0}
              disabledTooltip={I18n.t('EmployeeDrawer.GroupsTable.DisabledTooltip')}
              columns={chatsColumns}
              onCheckboxChange={onGroupCheckboxChange}
              buttonText={I18n.t('EmployeeDrawer.Add channel')}
              placeholder={I18n.t('EmployeeDrawer.Start typing')}
              name="channels"
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default memo(ChannelsControls);
